import { Link, useLocation } from 'react-router';
import { AlertTriangle, Building2, FileText, LineChart, ShieldCheck, Waypoints } from 'lucide-react';
import { useEnterpriseOrg } from '../context/EnterpriseOrgContext';
import { EnterpriseWorkspaceNav } from '../components/enterprise/EnterpriseWorkspaceNav';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useEnterpriseWorkspaceRole } from '../hooks/use-enterprise-workspace-role';
import { useEnterpriseWorkspaceCheckIns } from '../hooks/use-enterprise-workspace-checkins';
import { useEnterpriseWorkspaceInterventions } from '../hooks/use-enterprise-workspace-interventions';
import { useEnterpriseWorkspaceRenewalPreview } from '../hooks/use-enterprise-workspace-renewal-preview';
import { useEnterpriseWorkspaceQbrExcerpts } from '../hooks/use-enterprise-workspace-qbr-excerpts';
import { workspacePath } from '../lib/enterprise-work-routes';
import { useEnterpriseCapabilities } from '../hooks/use-enterprise-capabilities';

function qbrHasContent(ex: {
  renewalNarrativeNotes: string;
  expansionTargetNotes: string;
  qbrCadenceNotes: string;
  crossFunctionalOwnersNotes: string;
}): boolean {
  return [ex.renewalNarrativeNotes, ex.expansionTargetNotes, ex.qbrCadenceNotes, ex.crossFunctionalOwnersNotes].some(
    (s) => typeof s === 'string' && s.trim().length > 0,
  );
}

export default function EnterpriseWorkspaceHome() {
  const location = useLocation();
  const { activeOrgId, activeMembership } = useEnterpriseOrg();
  const { role } = useEnterpriseWorkspaceRole();
  const capabilities = useEnterpriseCapabilities();
  const { rows, loading, error, scope } = useEnterpriseWorkspaceCheckIns();
  const canUseAdminShortcuts =
    capabilities.hasCapability('workspace.policy.write') ||
    capabilities.hasCapability('connectors.configure') ||
    capabilities.hasCapability('governance.read');

  const interventions = useEnterpriseWorkspaceInterventions(30);
  const renewal = useEnterpriseWorkspaceRenewalPreview();
  const qbr = useEnterpriseWorkspaceQbrExcerpts();
  const avg = rows.length ? Math.round(rows.reduce((sum, r) => sum + r.rtiScore, 0) / rows.length) : 0;
  const highRate = rows.length ? Math.round((rows.filter((r) => r.rtiScore >= 61).length / rows.length) * 100) : 0;
  const recentCount = rows.filter((r) => Date.now() - new Date(r.occurredAt).getTime() <= 7 * 86400000).length;

  const roleBrief =
    role === 'executive'
      ? 'Executive view focuses on trend movement, risk posture, and rollout readiness.'
      : role === 'hr'
        ? 'HR view focuses on intervention operations, cohort safety, and support workload.'
        : role === 'manager'
          ? 'Manager view focuses on team-level stability and practical support cadence.'
          : 'Partner view focuses on workflow quality, service coordination, and escalation signals.';

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(14,165,233,0.18),transparent_30%),linear-gradient(180deg,#020617_0%,#0b1220_100%)]">
      <EnterpriseWorkspaceNav />
      <div className="mx-auto max-w-5xl space-y-5 px-4 py-6">
        <header className="rounded-xl border border-slate-600/80 bg-slate-900/82 p-5 shadow-xl shadow-black/25">
          <p className="text-xs uppercase tracking-wide text-slate-400">Standalone enterprise user app</p>
          <h1 className="mt-1 text-2xl font-bold text-slate-100">Workspace home</h1>
          <p className="mt-2 text-sm text-slate-300">
            Org: {activeMembership?.organizations?.name || 'No org selected'} {activeOrgId ? `(${activeOrgId.slice(0, 8)}...)` : ''}
          </p>
          <p className="mt-1 text-sm text-blue-200">Role profile: {role}</p>
          {scope ? <p className="mt-1 text-xs text-blue-300">Data scope: {scope}</p> : null}
          <p className="mt-2 text-sm text-slate-300">{roleBrief}</p>
          {loading ? <p className="mt-2 text-xs text-slate-400">Loading server-backed check-ins…</p> : null}
          {error ? <p className="mt-2 text-xs text-amber-300">Server data warning: {error}</p> : null}
        </header>

        <section className="grid gap-3 md:grid-cols-3">
          <Card className="border-slate-600/80 bg-slate-900/82 p-4 text-slate-100">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-slate-400">Check-ins (workspace)</p>
              <Building2 className="h-4 w-4 text-blue-300" />
            </div>
            <p className="mt-2 text-2xl font-semibold">{rows.length}</p>
          </Card>
          <Card className="border-slate-600/80 bg-slate-900/82 p-4 text-slate-100">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-slate-400">Average RTI</p>
              <LineChart className="h-4 w-4 text-blue-300" />
            </div>
            <p className="mt-2 text-2xl font-semibold">{rows.length ? avg : '—'}</p>
          </Card>
          <Card className="border-slate-600/80 bg-slate-900/82 p-4 text-slate-100">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-slate-400">High tension rate</p>
              <ShieldCheck className="h-4 w-4 text-blue-300" />
            </div>
            <p className="mt-2 text-2xl font-semibold">{rows.length ? `${highRate}%` : '—'}</p>
          </Card>
        </section>

        <section className="grid gap-3 md:grid-cols-2">
          <Card className="border-slate-700 bg-slate-900/80 p-4 text-slate-100">
            <p className="text-xs uppercase tracking-wide text-slate-400">7-day activity</p>
            <p className="mt-2 text-2xl font-semibold">{recentCount}</p>
            <p className="mt-1 text-sm text-slate-300">Check-ins logged in the last 7 days.</p>
          </Card>
          <Card className="border-slate-700 bg-slate-900/80 p-4 text-slate-100">
            <p className="text-xs uppercase tracking-wide text-slate-400">Recommended next action</p>
            <p className="mt-2 text-sm text-slate-200">
              {highRate >= 30
                ? 'Risk posture elevated: run daily check-ins and trigger intervention review.'
                : 'Risk posture stable: keep weekly cadence and monitor cohort drift.'}
            </p>
          </Card>
        </section>

        <section className="grid gap-3 md:grid-cols-2">
          <Card className="border-slate-700 bg-slate-900/80 p-4 text-slate-100">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-slate-400">Intervention queue (30d)</p>
              <AlertTriangle className="h-4 w-4 text-amber-300" />
            </div>
            {interventions.loading ? <p className="mt-2 text-sm text-slate-300">Loading intervention queue…</p> : null}
            {interventions.error ? <p className="mt-2 text-sm text-amber-300">{interventions.error}</p> : null}
            {!interventions.loading && !interventions.error ? (
              <>
                <p className="mt-2 text-2xl font-semibold">{interventions.queue.length}</p>
                <p className="mt-1 text-sm text-slate-300">
                  {interventions.queue.length > 0
                    ? `${interventions.queue[0].severity.toUpperCase()} priority event at top of queue.`
                    : 'No high-tension interventions currently flagged.'}
                </p>
                {interventions.scope ? (
                  <p className="mt-1 text-xs text-slate-400">Queue scope: {interventions.scope}</p>
                ) : null}
                {typeof interventions.responseSlaHours === 'number' ? (
                  <p className="mt-1 text-xs text-slate-400">Response SLA: {interventions.responseSlaHours}h</p>
                ) : null}
                {typeof interventions.slaBreaches === 'number' ? (
                  <p className="mt-1 text-xs text-amber-300">Breaches: {interventions.slaBreaches}</p>
                ) : null}
              </>
            ) : null}
          </Card>
          <Card className="border-slate-700 bg-slate-900/80 p-4 text-slate-100">
            <p className="text-xs uppercase tracking-wide text-slate-400">Role-directed action</p>
            <p className="mt-2 text-sm text-slate-200">
              {interventions.roleAction ||
                'Capture intervention context and escalate high-risk patterns through your org playbook.'}
            </p>
            {interventions.windowDays ? (
              <p className="mt-2 text-xs text-slate-400">Window: last {interventions.windowDays} days</p>
            ) : null}
          </Card>
        </section>

        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Renewal & expansion (Phase 10)</h2>
          <Card className="border-slate-700 bg-slate-900/80 p-4 text-slate-100">
            {renewal.loading ? (
              <p className="text-sm text-slate-400">Loading renewal signals…</p>
            ) : renewal.error ? (
              <p className="text-sm text-slate-400">
                {renewal.error.includes('owner') || renewal.error.includes('analyst')
                  ? `${renewal.error} (workspace preview is limited to owner, admin, and analyst roles.)`
                  : renewal.error}
              </p>
            ) : renewal.preview ? (
              <>
                <p className="text-xs text-slate-400">
                  Generated {new Date(renewal.preview.generatedAt).toLocaleString()} · same engine as Command Center
                </p>
                <div className="mt-3 grid gap-2 text-sm md:grid-cols-3">
                  <div>
                    <p className="text-xs uppercase text-slate-500">Utilization</p>
                    <p className="font-medium text-slate-100">
                      {renewal.preview.summary.utilizationPct != null
                        ? `${renewal.preview.summary.utilizationPct}%`
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-slate-500">Active seats</p>
                    <p className="font-medium text-slate-100">{renewal.preview.summary.active}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-slate-500">Days to renewal</p>
                    <p className="font-medium text-slate-100">
                      {renewal.preview.summary.daysToContractRenewal != null
                        ? renewal.preview.summary.daysToContractRenewal
                        : '—'}
                    </p>
                  </div>
                </div>
                {renewal.preview.signals.length > 0 ? (
                  <ul className="mt-3 space-y-2 border-t border-slate-700 pt-3 text-sm text-slate-300">
                    {renewal.preview.signals.slice(0, 4).map((s) => (
                      <li key={s.id}>
                        <span
                          className={
                            s.severity === 'critical'
                              ? 'text-rose-300'
                              : s.severity === 'warning'
                                ? 'text-amber-200'
                                : 'text-slate-200'
                          }
                        >
                          {s.title}
                        </span>
                        <span className="text-slate-500"> — {s.detail}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm text-slate-400">No renewal/expansion signals at this time.</p>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-400">No renewal data.</p>
            )}
          </Card>
        </section>

        {qbr.featureEnabled ? (
          <section className="space-y-2">
            <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-400">
              <FileText className="h-4 w-4 text-cyan-400" aria-hidden />
              QBR excerpts (Wave 9 KV)
            </h2>
            <Card className="border border-cyan-900/50 bg-slate-900/80 p-4 text-slate-100">
              {qbr.loading ? (
                <p className="text-sm text-slate-400">Loading account QBR notes…</p>
              ) : qbr.error ? (
                <p className="text-sm text-slate-400">{qbr.error}</p>
              ) : qbr.data && qbrHasContent(qbr.data.excerpts) ? (
                <>
                  <p className="text-xs text-slate-400">
                    Visibility: {qbr.data.visibility === 'full' ? 'full narrative' : 'renewal + QBR cadence only'} ·{' '}
                    {qbr.data.updatedAt
                      ? `Updated ${new Date(qbr.data.updatedAt).toLocaleString()}`
                      : 'No update timestamp'}
                  </p>
                  <div className="mt-3 space-y-4 text-sm">
                    {qbr.data.excerpts.renewalNarrativeNotes.trim() ? (
                      <div>
                        <p className="text-xs font-medium uppercase text-slate-500">Renewal narrative</p>
                        <p className="mt-1 whitespace-pre-wrap text-slate-200">{qbr.data.excerpts.renewalNarrativeNotes}</p>
                      </div>
                    ) : null}
                    {qbr.data.excerpts.qbrCadenceNotes.trim() ? (
                      <div>
                        <p className="text-xs font-medium uppercase text-slate-500">QBR cadence</p>
                        <p className="mt-1 whitespace-pre-wrap text-slate-200">{qbr.data.excerpts.qbrCadenceNotes}</p>
                      </div>
                    ) : null}
                    {qbr.data.visibility === 'full' && qbr.data.excerpts.expansionTargetNotes.trim() ? (
                      <div>
                        <p className="text-xs font-medium uppercase text-slate-500">Expansion targets</p>
                        <p className="mt-1 whitespace-pre-wrap text-slate-200">{qbr.data.excerpts.expansionTargetNotes}</p>
                      </div>
                    ) : null}
                    {qbr.data.visibility === 'full' && qbr.data.excerpts.crossFunctionalOwnersNotes.trim() ? (
                      <div>
                        <p className="text-xs font-medium uppercase text-slate-500">Cross-functional owners</p>
                        <p className="mt-1 whitespace-pre-wrap text-slate-200">
                          {qbr.data.excerpts.crossFunctionalOwnersNotes}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-400">
                  No QBR notes in Command Center yet. Owners can add them under Wave 9 — Strategic account growth.
                </p>
              )}
            </Card>
          </section>
        ) : null}

        {activeOrgId && canUseAdminShortcuts ? (
          <section className="space-y-2">
            <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-400">
              <Waypoints className="h-4 w-4 text-violet-400" aria-hidden />
              Admin shortcuts (Phase 11)
            </h2>
            <Card className="border border-violet-900/40 bg-slate-900/80 p-4 text-slate-100">
              <p className="text-sm text-slate-400">
                Opens Command Center waves with this organization in the URL. You still need an{' '}
                <span className="text-slate-200">admin session</span> for those pages.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button asChild variant="outline" size="sm" className="border-slate-600 bg-slate-900 text-slate-100">
                  <Link to={`/enterprise/admin/wave-8?org=${encodeURIComponent(activeOrgId)}`}>Wave 8 — Optimization</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="border-slate-600 bg-slate-900 text-slate-100">
                  <Link to={`/enterprise/admin/integration-hub?org=${encodeURIComponent(activeOrgId)}`}>Integration hub</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="border-slate-600 bg-slate-900 text-slate-100">
                  <Link to={`/enterprise/admin/wave-9?org=${encodeURIComponent(activeOrgId)}`}>Wave 9 — Account growth</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="border-slate-600 bg-slate-900 text-slate-100">
                  <Link to={`/enterprise/admin/governance-center?org=${encodeURIComponent(activeOrgId)}`}>Governance center</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="border-slate-600 bg-slate-900 text-slate-100">
                  <Link to={`/enterprise/admin/wave-10?org=${encodeURIComponent(activeOrgId)}`}>Wave 10 — Workspace</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="border-slate-600 bg-slate-900 text-slate-100">
                  <Link to={`/enterprise/admin/wave-11?org=${encodeURIComponent(activeOrgId)}`}>Wave 11 — Continuity</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="border-slate-600 bg-slate-900 text-slate-100">
                  <Link to={`/enterprise/admin/wave-12?org=${encodeURIComponent(activeOrgId)}`}>Wave 12 — Bidirectional</Link>
                </Button>
              </div>
            </Card>
          </section>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button asChild className="bg-blue-600 text-white">
            <Link to={workspacePath(location.pathname, 'check-in')}>Run enterprise check-in</Link>
          </Button>
          <Button asChild variant="outline" className="border-slate-600 bg-slate-900 text-slate-100">
            <Link to={workspacePath(location.pathname, 'insights')}>Open insights</Link>
          </Button>
          <Button asChild variant="outline" className="border-slate-600 bg-slate-900 text-slate-100">
            <Link to={workspacePath(location.pathname, 'interventions')}>Open interventions</Link>
          </Button>
          <Button asChild variant="outline" className="border-slate-600 bg-slate-900 text-slate-100">
            <Link to="/enterprise/admin">Open enterprise admin</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

